import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AuthContext = createContext();

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
};

// Mock user data for frontend-only auth
const mockUsers = [
  {
    id: 1,
    email: 'demo@example.com',
    password: 'demo123',
    name: 'Demo User',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    joinDate: '2023-01-15',
    totalOrders: 12,
    wishlistCount: 5
  },
  {
    id: 2,
    email: 'john@example.com',
    password: 'john123',
    name: 'John Smith',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    joinDate: '2023-03-22',
    totalOrders: 8,
    wishlistCount: 3
  }
];

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from localStorage on app start
  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      } catch (error) {
        localStorage.removeItem('currentUser');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const login = async (email, password) => {
    dispatch({ type: 'LOGIN_START' });
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check both mock users and saved users
    const savedUsers = JSON.parse(localStorage.getItem('jrweb_global_users') || '[]');
    const allUsers = [...mockUsers, ...savedUsers];
    const user = allUsers.find(u => u.email === email && u.password === password);
    
    if (user) {
      const userWithoutPassword = { ...user };
      delete userWithoutPassword.password;
      
      // Load user-specific data if it exists
      const userProfile = JSON.parse(localStorage.getItem(`jrweb_user_${user.id}_profile`) || 'null');
      const finalUser = userProfile || userWithoutPassword;
      
      localStorage.setItem('currentUser', JSON.stringify(finalUser));
      dispatch({ type: 'LOGIN_SUCCESS', payload: finalUser });
      return { success: true };
    } else {
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: 'Invalid email or password' };
    }
  };

  const register = async (userData) => {
    dispatch({ type: 'LOGIN_START' });
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Load existing users from localStorage
    const savedUsers = JSON.parse(localStorage.getItem('jrweb_global_users') || '[]');
    const allUsers = [...mockUsers, ...savedUsers];
    
    // Check if user already exists
    const existingUser = allUsers.find(u => u.email === userData.email);
    if (existingUser) {
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: 'User already exists with this email' };
    }
    
    // Create new user with default profile icon
    const newUser = {
      id: Date.now(), // Use timestamp for unique ID
      email: userData.email,
      name: userData.name,
      avatar: null, // Start with no avatar - will show default icon
      joinDate: new Date().toISOString().split('T')[0],
      totalOrders: 0,
      wishlistCount: 0,
      preferences: {
        notifications: true,
        newsletter: false
      }
    };
    
    // Save user to global users list
    const updatedUsers = [...savedUsers, { ...newUser, password: userData.password }];
    localStorage.setItem('jrweb_global_users', JSON.stringify(updatedUsers));
    
    // Initialize user-specific data
    localStorage.setItem(`jrweb_user_${newUser.id}_profile`, JSON.stringify(newUser));
    localStorage.setItem(`jrweb_user_${newUser.id}_orders`, JSON.stringify([]));
    localStorage.setItem(`jrweb_user_${newUser.id}_cart`, JSON.stringify([]));
    localStorage.setItem(`jrweb_user_${newUser.id}_wishlist`, JSON.stringify([]));
    
    localStorage.setItem('currentUser', JSON.stringify(newUser));
    dispatch({ type: 'LOGIN_SUCCESS', payload: newUser });
    return { success: true };
  };

  const logout = () => {
    localStorage.removeItem('currentUser');
    dispatch({ type: 'LOGOUT' });
  };

  const updateProfile = (updates) => {
    const updatedUser = { ...state.user, ...updates };
    
    // Update current user session
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    
    // Update user-specific profile data
    localStorage.setItem(`jrweb_user_${updatedUser.id}_profile`, JSON.stringify(updatedUser));
    
    // Update global users list if user exists there
    const savedUsers = JSON.parse(localStorage.getItem('jrweb_global_users') || '[]');
    const updatedUsers = savedUsers.map(user => 
      user.id === updatedUser.id 
        ? { ...user, ...updates, password: user.password } // Keep password intact
        : user
    );
    localStorage.setItem('jrweb_global_users', JSON.stringify(updatedUsers));
    
    dispatch({ type: 'UPDATE_PROFILE', payload: updates });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    mockUsers: mockUsers.map(u => ({ ...u, password: undefined })) // For demo purposes
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
